'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import LogsTab from 'src/parts/tabs/LogsTab';
import { getUserLogs } from 'src/api/admin/logs';
import { EventEmitter, guardedClient as request } from '@repo/shared/helpers';
import { startOfDay, subMonths, endOfDay } from 'date-fns';
import { useMemo } from 'react';
import { useConfig } from 'src/hooks/useConfig';

export const Route = createFileRoute('/_admin/users/$userId/logs')({
	validateSearch: (search: Record<string, unknown>) => {
		const now = new Date();

		return {
			from: (search.from as string) || startOfDay(subMonths(now, 1)).toISOString(),
			until: (search.until as string) || endOfDay(now).toISOString(),
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || 'timestamp:desc',
			type: (search.type as string) || 'UserLog',
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const logs = await getUserLogs(userId, deps);
		return { logs };
	},
	component: LogsTabPage,
});

function LogsTabPage() {
	const { userId } = Route.useParams();
	const queryClient = useQueryClient();
	const searchParams = Route.useSearch();
	const { config } = useConfig();
	const useAntiPassback = !!config?.featureFlags?.USES_ANTI_PASSBACK;
	const { logs: initialLogs } = Route.useLoaderData();

	const { data: logs, refetch: refetchLogs } = useQuery({
		queryKey: ['user-logs', userId, searchParams],
		queryFn: () => getUserLogs(userId, searchParams),
		initialData: initialLogs,
	});

	const handleReload = () => {
		refetchLogs();
	};

	const handleResolveAntiPassback = async (log: Record<string, unknown>) => {
		const isDoubleExit = log.error === 'ANTI_PASSBACK_TRIGGERED_OUT';
		const direction = isDoubleExit ? 'in' : 'out';
		const actionLabel = isDoubleExit ? 'Check-in' : 'Checkout';

		let actionOk = false;
		try {
			await request.post('/api/logs/access', {
				userId: log.userId,
				zoneId: log.zoneId,
				direction,
			});
			actionOk = true;
		} catch (err: unknown) {
			const error = err as Error;
			EventEmitter.emit('toast', { type: 'error', message: `${actionLabel} failed: ${error.message}` });
		}

		if (actionOk) {
			if (useAntiPassback) {
				try {
					const { body: data } = await request.post(`/api/users/${log.userId}/clear-anti-passback`, {
						siteId: log.siteId,
					});
					EventEmitter.emit('toast', {
						type: 'success',
						message: `${actionLabel} complete. Anti-passback cleared on ${data.cleared} of ${data.total} devices`,
					});
				} catch (err) {
					EventEmitter.emit('toast', {
						type: 'warning',
						message: `${actionLabel} complete but could not clear anti-passback on any device`,
					});
				}
			} else {
				EventEmitter.emit('toast', { type: 'success', message: `${actionLabel} complete` });
			}
		}

		handleReload();
	};

	return (
		<LogsTab
			logType="UserLog"
			logs={logs}
			onReload={handleReload}
			userId={userId}
			onAccessLogCheckout={handleResolveAntiPassback}
		/>
	);
}
